import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Card, CardBody, CardHeader, CardTitle, Col, Input, Media, Row, Spinner, Table } from "reactstrap";
import Button from "react-bootstrap/Button";
import { Tag, TrendingDown, TrendingUp, Layers } from "react-feather";

import { apiFetch, API, getMarket } from "../../services/api";
import {
    BAND_ORDER,
    bandTone,
    cheaperShare,
    distinctValues,
    filterProducts,
    formatEur,
    formatRatio,
    formatShare,
    formatUpdatedAt,
    sortProducts,
} from "../../helpers/priceBenchmarks";

const PAGE_SIZE = 100;

function Counter({ icon: Icon, tone, label, value, hint, loading }) {
    return (
        <Col md="3">
            <Card className="flex-fill">
                <CardBody className="py-4">
                    <Media>
                        <div className="d-inline-block mt-2 mr-3">
                            <Icon className={"feather-lg text-" + tone} />
                        </div>
                        {loading ? (
                            <Media body>
                                <div className="mb-0">
                                    <Spinner size="sm" />
                                </div>
                            </Media>
                        ) : (
                            <Media body>
                                <h3 className="mb-2">{value}</h3>
                                <div className="mb-0">{label}</div>
                                {hint ? <div className="text-muted small">{hint}</div> : null}
                            </Media>
                        )}
                    </Media>
                </CardBody>
            </Card>
        </Col>
    );
}

function GroupTable({ title, rows, nameLabel, onPick, picked }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle tag="h5" className="mb-0">{title}</CardTitle>
            </CardHeader>
            <Table size="sm" hover className="mb-0">
                <thead>
                    <tr>
                        <th>{nameLabel}</th>
                        <th className="text-right">Produits</th>
                        <th className="text-right">Écart médian</th>
                        <th className="text-right">≥ 3 % moins chers</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan="4" className="text-muted">Aucune donnée</td></tr>
                    ) : rows.map((g) => (
                        <tr key={g.name} onClick={() => onPick(g.name)} style={{ cursor: "pointer" }} className={picked === g.name ? "table-active" : ""}>
                            <td>{g.name}</td>
                            <td className="text-right">{g.count}</td>
                            <td className={"text-right text-" + (g.medianRatio <= -0.03 ? "success" : g.medianRatio >= 0.03 ? "danger" : "muted")}>{formatRatio(g.medianRatio)}</td>
                            <td className="text-right">{formatShare(g.cheaperShare)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Card>
    );
}

// « Prix vs marché » : notre meilleur prix face au prix de référence Google
// Shopping (moyenne pondérée des autres annonceurs pour le même produit).
// Source : table price_benchmarks, importée chaque matin depuis le Merchant
// Center (Market Insights). Le même écart pose le label « Prix … » du flux CSS.
export function PriceBenchmarks() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [brand, setBrand] = useState("");
    const [category, setCategory] = useState("");
    const [band, setBand] = useState("");
    const [query, setQuery] = useState("");
    const [sortKey, setSortKey] = useState("ratio");
    const [sortDir, setSortDir] = useState("asc");
    const [shown, setShown] = useState(PAGE_SIZE);
    // « referenced » : produits avec un prix de référence Google ;
    // « unreferenced » : publiés dans le flux mais sans référence (invisibles sinon).
    const [scope, setScope] = useState("referenced");
    const cancelled = useRef(false);
    const market = getMarket();

    useEffect(() => {
        cancelled.current = false;
        setData(null);
        setError(null);
        apiFetch(API.back + "/price-benchmarks", { method: "GET" })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((payload) => {
                if (cancelled.current) return;
                setData(payload && typeof payload === "object" ? payload : {});
            })
            .catch((err) => {
                console.log(err);
                if (cancelled.current) return;
                setError("Position prix indisponible (" + err.message + "). En Espagne, le rapport Google n'est pas encore activé.");
                setData({});
            });
        return () => {
            cancelled.current = true;
        };
    }, [market]);

    const loading = data === null && !error;
    const view = data || {};
    const referenced = Array.isArray(view.products) ? view.products : [];
    const unreferenced = Array.isArray(view.unreferenced) ? view.unreferenced : [];
    const products = scope === "unreferenced" ? unreferenced : referenced;
    const bands = Array.isArray(view.bands) ? view.bands : [];
    const brands = Array.isArray(view.brands) ? view.brands : [];
    const categories = Array.isArray(view.categories) ? view.categories : [];

    const brandOptions = useMemo(() => distinctValues(products, "brand"), [products]);
    const categoryOptions = useMemo(() => distinctValues(products, "category"), [products]);
    const filtered = useMemo(
        () => sortProducts(filterProducts(products, { brand, category, band: scope === "unreferenced" ? "" : band, query }),
            scope === "unreferenced" && (sortKey === "ratio" || sortKey === "benchmarkPrice") ? "offers" : sortKey,
            scope === "unreferenced" && sortKey === "ratio" ? "desc" : sortDir),
        [products, brand, category, band, query, sortKey, sortDir, scope]
    );
    const unreferencedShare = view.published ? unreferenced.length / view.published : 0;

    const dearer = bands.filter((b) => b.label === "Prix +3%" || b.label === "Prix +10%").reduce((s, b) => s + (b.count || 0), 0);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else {
            setSortKey(key);
            setSortDir(key === "title" || key === "brand" ? "asc" : "asc");
        }
        setShown(PAGE_SIZE);
    };
    const arrow = (key) => (sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "");

    return (
        <div>
            <Row className="mb-2 mb-xl-4">
                <Col xs="auto" className="d-none d-sm-block">
                    <h3>Prix vs marché Shopping</h3>
                    <p className="text-muted mb-0">
                        Notre meilleur prix face au prix de référence Google : la moyenne des autres annonceurs Shopping pour le même produit,
                        marché {market.toUpperCase()}. Mis à jour chaque matin ; dernière donnée : {formatUpdatedAt(view.updatedAt)}.
                    </p>
                </Col>
            </Row>

            {error ? <div className="alert alert-warning">{error}</div> : null}

            <Row>
                <Counter icon={Layers} tone="primary" label="produits avec un prix de référence Google" value={loading ? "" : view.total || 0}
                    hint={loading ? null : "sur " + (view.published || 0) + " publiés dans le flux (" + formatShare(view.coverage) + " couverts, " + unreferenced.length + " sans référence)"} loading={loading} />
                <Counter icon={TrendingDown} tone="success" label="écart médian au marché" value={loading ? "" : formatRatio(view.medianRatio)} hint="négatif = nous sommes moins chers" loading={loading} />
                <Counter icon={Tag} tone="info" label="produits ≥ 3 % moins chers" value={loading ? "" : formatShare(cheaperShare(bands))} loading={loading} />
                <Counter icon={TrendingUp} tone="danger" label="produits plus chers que le marché" value={loading ? "" : dearer} hint="à exclure ou brider dans les campagnes" loading={loading} />
            </Row>

            <Row>
                <Col lg="4">
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">Par tranche</CardTitle>
                        </CardHeader>
                        <Table size="sm" hover className="mb-0">
                            <thead>
                                <tr><th>Tranche (label du flux)</th><th className="text-right">Produits</th><th className="text-right">Part</th></tr>
                            </thead>
                            <tbody>
                                {BAND_ORDER.map((label) => {
                                    const b = bands.find((x) => x.label === label) || { count: 0, share: 0 };
                                    return (
                                        <tr key={label} onClick={() => { setBand(band === label ? "" : label); setShown(PAGE_SIZE); }} style={{ cursor: "pointer" }} className={band === label ? "table-active" : ""}>
                                            <td><Badge color={bandTone(label)}>{label}</Badge></td>
                                            <td className="text-right">{b.count}</td>
                                            <td className="text-right">{formatShare(b.share)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card>
                </Col>
                <Col lg="4">
                    <GroupTable title="Par marque" nameLabel="Marque" rows={brands.slice(0, 15)} picked={brand} onPick={(n) => { setBrand(brand === n ? "" : n); setShown(PAGE_SIZE); }} />
                </Col>
                <Col lg="4">
                    <GroupTable title="Par catégorie" nameLabel="Catégorie" rows={categories} picked={category} onPick={(n) => { setCategory(category === n ? "" : n); setShown(PAGE_SIZE); }} />
                </Col>
            </Row>

            <Card>
                <CardHeader>
                    <Row className="align-items-center mb-2">
                        <Col>
                            <Button variant={scope === "referenced" ? "primary" : "outline-primary"} size="sm" className="mr-2" onClick={() => { setScope("referenced"); setShown(PAGE_SIZE); }}>
                                Avec référence Google ({referenced.length})
                            </Button>
                            <Button variant={scope === "unreferenced" ? "primary" : "outline-primary"} size="sm" onClick={() => { setScope("unreferenced"); setShown(PAGE_SIZE); }}>
                                Sans référence Google ({unreferenced.length}, {formatShare(unreferencedShare)} du flux)
                            </Button>
                            {scope === "unreferenced" ? (
                                <span className="text-muted small ml-3">
                                    Publiés dans le flux CSS mais absents du rapport Google : la référence n'existe que si d'autres annonceurs vendent le même GTIN. Sans référence, pas de label « Prix … » dans les campagnes.
                                </span>
                            ) : null}
                        </Col>
                    </Row>
                    <Row className="align-items-center">
                        <Col md="3"><CardTitle tag="h5" className="mb-0">Produits ({filtered.length})</CardTitle></Col>
                        <Col md="3"><Input type="text" placeholder="Rechercher un produit…" value={query} onChange={(e) => { setQuery(e.target.value); setShown(PAGE_SIZE); }} /></Col>
                        <Col md="2">
                            <Input type="select" value={brand} onChange={(e) => { setBrand(e.target.value); setShown(PAGE_SIZE); }}>
                                <option value="">Toutes les marques</option>
                                {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                            </Input>
                        </Col>
                        <Col md="2">
                            <Input type="select" value={category} onChange={(e) => { setCategory(e.target.value); setShown(PAGE_SIZE); }}>
                                <option value="">Toutes les catégories</option>
                                {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Input>
                        </Col>
                        {scope === "referenced" ? (
                            <Col md="2">
                                <Input type="select" value={band} onChange={(e) => { setBand(e.target.value); setShown(PAGE_SIZE); }}>
                                    <option value="">Toutes les tranches</option>
                                    {BAND_ORDER.map((b) => <option key={b} value={b}>{b}</option>)}
                                </Input>
                            </Col>
                        ) : null}
                    </Row>
                </CardHeader>
                <Table size="sm" hover responsive className="mb-0">
                    <thead>
                        <tr>
                            <th onClick={() => toggleSort("title")} style={{ cursor: "pointer" }}>Produit{arrow("title")}</th>
                            <th onClick={() => toggleSort("brand")} style={{ cursor: "pointer" }}>Marque{arrow("brand")}</th>
                            <th>Catégorie</th>
                            <th className="text-right" onClick={() => toggleSort("price")} style={{ cursor: "pointer" }}>Notre prix{arrow("price")}</th>
                            {scope === "referenced" ? (
                                <>
                                    <th className="text-right" onClick={() => toggleSort("benchmarkPrice")} style={{ cursor: "pointer" }}>Marché{arrow("benchmarkPrice")}</th>
                                    <th className="text-right" onClick={() => toggleSort("ratio")} style={{ cursor: "pointer" }}>Écart{arrow("ratio")}</th>
                                    <th>Tranche</th>
                                </>
                            ) : (
                                <th className="text-right" onClick={() => toggleSort("offers")} style={{ cursor: "pointer" }}>Offres publiées{arrow("offers")}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7"><Spinner size="sm" /></td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan="7" className="text-muted">Aucun produit</td></tr>
                        ) : filtered.slice(0, shown).map((p) => (
                            <tr key={p.slug}>
                                <td>
                                    <a href={"https://app.therunningcollective." + market + "/product/" + p.slug} target="_blank" rel="noopener noreferrer">{p.title || p.slug}</a>
                                </td>
                                <td>{p.brand}</td>
                                <td>{p.category}</td>
                                <td className="text-right">{formatEur(p.price)}</td>
                                {scope === "referenced" ? (
                                    <>
                                        <td className="text-right">{formatEur(p.benchmarkPrice)}</td>
                                        <td className={"text-right text-" + (p.ratio <= -0.03 ? "success" : p.ratio >= 0.03 ? "danger" : "muted")}>{formatRatio(p.ratio)}</td>
                                        <td><Badge color={bandTone(p.label)}>{p.label}</Badge></td>
                                    </>
                                ) : (
                                    <td className="text-right">{p.offers}</td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {filtered.length > shown ? (
                    <CardBody className="text-center">
                        <Button variant="outline-primary" size="sm" onClick={() => setShown(shown + PAGE_SIZE)}>
                            Afficher {Math.min(PAGE_SIZE, filtered.length - shown)} produits de plus
                        </Button>
                    </CardBody>
                ) : null}
            </Card>
        </div>
    );
}

export default PriceBenchmarks;
